/**
 * Löst eine preview3d-v2-Konfiguration (Kategorie → Produkt) plus gewählte Varianten zu einem
 * konkreten Aufbau auf. Rein, ohne three.js – Node-Tests in test/preview3d.resolve.test.ts.
 */
import { defaultBody } from './defaults.ts'
import type {
  BodyKind,
  EdgeMode,
  HangingKind,
  Preview3dBody,
  Preview3dConfigV2,
  Preview3dFrame,
  Preview3dLibrary,
  Preview3dRule,
  ResolvedFrame,
  ResolvedPreview3d,
  SurfaceFinish,
} from './types.ts'

const BODY_KINDS: BodyKind[] = ['canvas', 'plate', 'acrylic', 'paper']
const EDGES: EdgeMode[] = ['mirror', 'wrap', 'white', 'color']
const SURFACES: SurfaceFinish[] = ['matt', 'satin', 'gloss']
const HANGINGS: HangingKind[] = ['none', 'rail', 'cord', 'hooks']

export function isPreview3dV2(raw: unknown): raw is Preview3dConfigV2 {
  return !!raw && typeof raw === 'object' && Number((raw as any).v) === 2
}

/** Normalisiert eine Rohkonfiguration (Admin-JSON) – unbekannte Felder fallen weg */
export function normalizePreview3dConfig(raw: unknown): Preview3dConfigV2 | null {
  if (!isPreview3dV2(raw)) return null
  const r = raw as any
  const out: Preview3dConfigV2 = { v: 2 }
  if (r.body && typeof r.body === 'object') out.body = cleanBody(r.body)
  if ('frame' in r) out.frame = cleanFrame(r.frame)
  if (Array.isArray(r.rules)) out.rules = r.rules.map(cleanRule).filter((x: Preview3dRule | null): x is Preview3dRule => !!x)
  if (Array.isArray(r.rooms)) out.rooms = r.rooms.map(String).filter(Boolean)
  return out
}

/**
 * Mehrere Ebenen (Kategorie, Elternkategorie …, Produkt) zusammenführen; spätere gewinnen.
 * body: feldweise, frame: ersetzt (auch null), rules: angehängt (Produktregeln zuletzt = gewinnen).
 */
export function mergePreview3dConfigs(...layers: Array<unknown>): Preview3dConfigV2 | null {
  let out: Preview3dConfigV2 | null = null
  for (const layer of layers) {
    const cfg = normalizePreview3dConfig(layer)
    if (!cfg) continue
    if (!out) {
      out = { v: 2, body: { ...(cfg.body ?? {}) }, rules: [...(cfg.rules ?? [])] }
      if ('frame' in cfg) out.frame = cfg.frame ?? null
      if (cfg.rooms?.length) out.rooms = cfg.rooms
      continue
    }
    out.body = { ...(out.body ?? {}), ...(cfg.body ?? {}) }
    if ('frame' in cfg) out.frame = cfg.frame ?? null
    out.rules = [...(out.rules ?? []), ...(cfg.rules ?? [])]
    if (cfg.rooms?.length) out.rooms = cfg.rooms
  }
  return out
}

/** Alle Varianten-IDs, auf die Regeln reagieren (für die Auswahl-Chips in der 3D-Ansicht) */
export function ruleVariantIds(cfg: Preview3dConfigV2 | null): Set<string> {
  const ids = new Set<string>()
  for (const r of cfg?.rules ?? []) for (const id of r.variantIds) ids.add(String(id))
  return ids
}

export function resolvePreview3dState(
  cfg: Preview3dConfigV2 | null,
  selectedVariantIds: Iterable<string | number>,
  library: Preview3dLibrary,
): ResolvedPreview3d {
  const selected = new Set([...selectedVariantIds].map(String))
  const bodyOver: Partial<Preview3dBody> = {}
  let frameOver: { set: boolean; value: Preview3dFrame | null } = { set: false, value: null }
  const applied: string[] = []

  for (const rule of cfg?.rules ?? []) {
    const hit = rule.variantIds.find((id) => selected.has(String(id)))
    if (!hit) continue
    applied.push(String(hit))
    for (const [path, value] of Object.entries(rule.set)) {
      if (path.startsWith('body.')) {
        Object.assign(bodyOver, cleanBody({ [path.slice(5)]: value }))
      } else if (path === 'frame') {
        frameOver = { set: true, value: cleanFrame(value) }
      } else if (path.startsWith('frame.')) {
        const base: Preview3dFrame = frameOver.set ? (frameOver.value ?? { profile: '' }) : { ...(cfg?.frame ?? { profile: '' }) }
        const merged = cleanFrame({ ...base, [path.slice(6)]: value })
        frameOver = { set: true, value: merged }
      }
    }
  }

  const kind = bodyOver.kind ?? cfg?.body?.kind ?? 'canvas'
  const body: Preview3dBody = { ...defaultBody(kind), ...stripUndef(cfg?.body ?? {}), ...stripUndef(bodyOver), kind }
  const frameCfg = frameOver.set ? frameOver.value : (cfg?.frame ?? null)

  return { body, frame: resolveFrame(frameCfg, library), appliedVariantIds: applied }
}

function resolveFrame(frame: Preview3dFrame | null, library: Preview3dLibrary): ResolvedFrame | null {
  if (!frame?.profile) return null
  const p = library.profiles.find((x) => x.slug === frame.profile)
  if (!p) return null
  const glass = frame.glass === false ? '' : frame.glass === true ? (p.glassMaterial || 'glas') : p.glassMaterial
  const matWidthMm = num(frame.matWidthMm, p.matWidthMm)
  return {
    ...p,
    gapMm: num(frame.gapMm, p.gapMm),
    matWidthMm,
    matLongMm: num(frame.matLongMm, matWidthMm),
    matShortMm: num(frame.matShortMm, matWidthMm),
    glassMaterial: glass,
  }
}

function cleanBody(raw: any): Partial<Preview3dBody> {
  const b: Partial<Preview3dBody> = {}
  if (!raw || typeof raw !== 'object') return b
  if (BODY_KINDS.includes(raw.kind)) b.kind = raw.kind
  if (isNum(raw.thicknessMm)) b.thicknessMm = clamp(Number(raw.thicknessMm), 0.1, 200)
  if (isNum(raw.barWidthMm)) b.barWidthMm = clamp(Number(raw.barWidthMm), 10, 120)
  if (EDGES.includes(raw.edge)) b.edge = raw.edge
  if (typeof raw.edgeColor === 'string' && raw.edgeColor) b.edgeColor = raw.edgeColor
  if (SURFACES.includes(raw.surface)) b.surface = raw.surface
  if (HANGINGS.includes(raw.hanging)) b.hanging = raw.hanging
  for (const k of ['faceMaterial', 'edgeMaterial', 'backMaterial', 'supportMaterial'] as const) {
    if (typeof raw[k] === 'string' && raw[k]) b[k] = raw[k]
  }
  return b
}

function cleanFrame(raw: unknown): Preview3dFrame | null {
  if (raw == null || raw === '' || raw === false || raw === 'none') return null
  if (typeof raw === 'string') return { profile: raw }
  if (typeof raw !== 'object') return null
  const r = raw as any
  const profile = String(r.profile ?? '').trim()
  if (!profile || profile === 'none') return null
  const f: Preview3dFrame = { profile }
  if (isNum(r.gapMm)) f.gapMm = clamp(Number(r.gapMm), 0, 50)
  if (isNum(r.matWidthMm)) f.matWidthMm = clamp(Number(r.matWidthMm), 0, 200)
  if (isNum(r.matLongMm)) f.matLongMm = clamp(Number(r.matLongMm), 0, 200)
  if (isNum(r.matShortMm)) f.matShortMm = clamp(Number(r.matShortMm), 0, 200)
  if (typeof r.glass === 'boolean') f.glass = r.glass
  return f
}

function cleanRule(raw: any): Preview3dRule | null {
  if (!raw || typeof raw !== 'object') return null
  const variantIds = (Array.isArray(raw.variantIds) ? raw.variantIds : [raw.variantId]).map((v: unknown) => String(v ?? '').trim()).filter(Boolean)
  if (!variantIds.length || !raw.set || typeof raw.set !== 'object') return null
  return { variantIds, set: { ...raw.set }, ...(raw.label ? { label: String(raw.label) } : {}) }
}

function stripUndef<T extends object>(o: T): Partial<T> {
  const out: Partial<T> = {}
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== null && v !== '') (out as any)[k] = v
  return out
}

const isNum = (v: unknown) => v !== null && v !== '' && v !== undefined && Number.isFinite(Number(v))
const num = (v: unknown, fallback: number) => (isNum(v) ? Number(v) : fallback)
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))
